import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube } from "lucide-react";
import logo from "@/assets/logo.png";

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const ThreadsIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.74-1.754-.51-.586-1.296-.883-2.336-.89h-.032c-.84 0-1.98.22-2.706 1.316L6.5 8.095c.98-1.475 2.57-2.274 4.59-2.274h.052c3.368.02 5.374 2.059 5.574 5.605.115.046.23.095.342.145 1.566.722 2.712 1.815 3.313 3.16.841 1.874.918 4.93-1.596 7.442-1.82 1.817-4.047 2.656-7.079 2.676Zm1.36-11.296c-.204 0-.416.006-.62.018-1.92.104-3.084.828-3.01 2.112.082 1.397 1.723 1.983 3.262 1.9 1.355-.069 2.98-.576 3.253-3.892a10.44 10.44 0 0 0-2.885-.138Z" />
  </svg>
);

const Footer = () => {
  const socialLinks = [
    { href: "https://www.linkedin.com/in/marquetta-bryant-9548a817a/", icon: Linkedin, ariaLabel: "LinkedIn" },
    { href: "https://www.instagram.com/marquetta_fvg/", icon: Instagram, ariaLabel: "Instagram" },
    { href: "https://www.threads.com/@marquetta_fvg", icon: ThreadsIcon, ariaLabel: "Threads" },
    { href: "https://youtube.com/@marquettabryant?si=4NeDYjz2Js0HhNeV", icon: Youtube, ariaLabel: "YouTube" },
    { href: "https://www.tiktok.com/@marquetta_bryant", icon: TikTokIcon, ariaLabel: "TikTok" },
  ];

  const wealthLinks = [
    { label: "The Marquetta Method", href: "/about" },
    { label: "Real Estate Strategy", href: "/real-estate" },
    { label: "Mortgage & Capital", href: "/mortgage" },
    { label: "Market Intel", href: "/academy/tools" },
  ];

  const businessLinks = [
    { label: "Fresh Vision Academy", href: "https://freshvisionacademy.org/", external: true },
    { label: "Speaking & Keynotes", href: "/speaking" },
    { label: "Business Consulting", href: "/about" },
    { label: "Founder Resources", href: "/academy/tools" },
  ];

  return (
    <footer className="bg-secondary text-white" role="contentinfo">
      {/* Main Footer */}
      <div className="container mx-auto px-6 py-20 md:py-24">
        <div className="flex flex-col items-center text-center">
          {/* Logo & Brand Statement */}
          <Link to="/" aria-label="Marquetta Bryant — home">
            <img src={logo} alt="Marquetta Bryant logo" className="h-12 md:h-14 mb-5" />
          </Link>
          <p className="text-accent font-semibold uppercase tracking-[0.2em] text-sm md:text-base mb-14">
            Architect of Your Personal Economy
          </p>

          {/* Divider */}
          <div className="w-full max-w-4xl h-px bg-white/10 mb-14" />

          {/* Navigation Columns - Centered */}
          <div className="grid md:grid-cols-2 gap-14 md:gap-24 mb-14 w-full max-w-3xl">
            {/* Build Your Wealth */}
            <div className="text-center">
              <h3 className="text-accent font-semibold text-lg mb-1.5 tracking-wide">Build Your Wealth</h3>
              <p className="text-[#D1D5DB] text-sm mb-5">For Clients</p>
              <ul className="space-y-3">
                {wealthLinks.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="text-[#D1D5DB] hover:text-accent transition-colors text-[15px]">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Scale Your Business */}
            <div className="text-center">
              <h3 className="text-accent font-semibold text-lg mb-1.5 tracking-wide">Scale Your Business</h3>
              <p className="text-[#D1D5DB] text-sm mb-5">For Professionals</p>
              <ul className="space-y-3">
                {businessLinks.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#D1D5DB] hover:text-accent transition-colors text-[15px]"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-[#D1D5DB] hover:text-accent transition-colors text-[15px]">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Social Icons */}
          <div className="flex justify-center gap-6 mb-14">
            {socialLinks.map((social) => (
              <a key={social.ariaLabel} href={social.href} target="_blank" rel="noopener noreferrer me" className="group" aria-label={social.ariaLabel}>
                <div className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <social.icon className="w-5 h-5 text-accent/70 group-hover:text-accent transition-colors" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance Bar — NMLS + Broker License + Equal Housing */}
      <div className="border-t border-white/10 bg-[#081321]">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto text-center space-y-3">
            <p className="text-[#D1D5DB] text-sm">
              <strong className="text-accent">Marquetta Bryant</strong> — Mortgage Loan Originator,{" "}
              <a
                href="https://www.nmlsconsumeraccess.org/EntityDetails.aspx/INDIVIDUAL/2512081"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-accent transition-colors"
                aria-label="Verify NMLS #2512081 on NMLS Consumer Access"
              >
                NMLS #2512081
              </a>
              {" · "}Licensed through One Real Mortgage Corp.
            </p>
            <p className="text-[#D1D5DB] text-sm">
              Licensed Real Estate Broker, State of Georgia · <strong className="text-accent">Fresh Vision Realty</strong>
            </p>
            <p className="text-[#D1D5DB]/80 text-xs">
              <span aria-hidden="true">⌂</span> Equal Housing Opportunity · Equal Housing Lender
            </p>
            <p className="text-[#D1D5DB]/60 text-[11px] max-w-3xl mx-auto leading-relaxed pt-2">
              This website is for informational purposes only and does not constitute an offer to lend or a commitment to extend credit. All loan products are subject to qualification, underwriting guidelines, and applicable state and federal regulations. Licensing information available at{" "}
              <a
                href="https://www.nmlsconsumeraccess.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-accent transition-colors"
              >
                nmlsconsumeraccess.org
              </a>
              . Fresh Vision Realty and One Real Mortgage are separate entities.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-6 py-5">
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-8">
            <p className="text-[#D1D5DB]/50 text-xs">
              © {new Date().getFullYear()} Marquetta Bryant. All Rights Reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-[#D1D5DB]/40 hover:text-accent text-xs transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-[#D1D5DB]/40 hover:text-accent text-xs transition-colors">Terms</Link>
              <Link to="/contact" className="text-[#D1D5DB]/40 hover:text-accent text-xs transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
